import React, { useMemo } from 'react';
import JobBadge from './JobBadge';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const isOverdue = (due_date, status) => {
  if (!due_date || ['resolved', 'closed'].includes(status)) return false;
  return new Date(due_date) < new Date(new Date().toDateString());
};

const PRIORITY_ORDER = { urgent: 0, high: 1, normal: 2, low: 3 };
const byPriority = (a, b) =>
  (PRIORITY_ORDER[a.priority] ?? 2) - (PRIORITY_ORDER[b.priority] ?? 2);

const STAFF_COLUMNS = [
  'awaiting_assignment',
  'waiting_for_agent',
  'waiting_for_client',
  'resolved',
];
const CUSTOMER_COLUMNS = [
  'awaiting_assignment',
  'waiting_for_agent',
  'waiting_for_client',
  'resolved',
];

const KanbanBoard = ({ cases, staff, onNavigate, t, priorityChip, priorityKey, statusChip, showDueDate }) => {
  const columns = staff ? STAFF_COLUMNS : CUSTOMER_COLUMNS;

  const grouped = useMemo(() => {
    const map = {};
    columns.forEach(col => { map[col] = []; });
    cases.forEach(c => {
      if (map[c.status] !== undefined) map[c.status].push(c);
      else if (!columns.includes(c.status)) {
        // closed and unknown statuses: skip in kanban
      }
    });
    columns.forEach(col => map[col].sort(byPriority));
    return map;
  }, [cases, columns]);

  return (
    <div className="kb-board">
      {columns.map(col => (
        <div key={col} className="kb-column">
          <div className="kb-col-header">
            <span className={`cs-chip ${statusChip(col).split(' ')[1]}`}>{t(`caseStatus.${col}`)}</span>
            <span className="kb-count">{grouped[col].length}</span>
          </div>
          <div className="kb-cards">
            {grouped[col].length === 0 && (
              <div className="kb-empty">—</div>
            )}
            {grouped[col].map(c => (
              <div key={c.id} className={`kb-card${isOverdue(c.due_date, c.status) ? ' kb-card--overdue' : ''}`} onClick={() => onNavigate(`/cases/${c.id}`)}>
                <div className="kb-card-top">
                  <span className="kb-case-num">{c.case_number}</span>
                  <div className="kb-card-top-right">
                    {staff && <JobBadge count={c.active_job_count} leadStatus={c.lead_job_status} t={t} />}
                    <span className={priorityChip(c.priority)}>{t(priorityKey(c.priority || 'normal'))}</span>
                  </div>
                </div>
                <div className="kb-card-title">
                  {c.title}
                  {c.language && <span className="cs-lang-badge">{c.language.toUpperCase()}</span>}
                </div>
                {c.title_translation && c.title_translation_lang !== c.language && (
                  <div className="kb-card-translation">
                    <span className="kb-tr-lang">{c.title_translation_lang.toUpperCase()}</span>
                    {c.title_translation}
                  </div>
                )}
                {staff && c.customer_username && (
                  <div className="kb-card-meta">
                    <span className="kb-card-label">{t('cases.customer')}:</span> {c.customer_username}
                  </div>
                )}
                {c.agent_username && (
                  <div className="kb-card-meta kb-agent">
                    <span className="kb-card-label">{t('cases.agent')}:</span> {c.agent_username}
                  </div>
                )}
                <div className="kb-card-date">
                  <span className="kb-card-label">{t('dashboard.dateOpen')}:</span> {fmtDate(c.created_at)}
                </div>
                {showDueDate && c.due_date && (
                  <div className={`kb-card-date${isOverdue(c.due_date, c.status) ? ' kb-card-due--overdue' : ''}`}>
                    <span className="kb-card-label">{t('cases.dueDate')}:</span> {fmtDate(c.due_date)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default KanbanBoard;
